import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import { showToast } from '../components/Toast';
import { useAnalytics } from '../hooks/useAnalytics';

const SubscriptionSettings = () => {
  const { trackCheckoutStarted } = useAnalytics();
  const { user, subscriptionStatus } = useAuth();
  const { apiClient } = useAPI();
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [isProcessing, setIsProcessing] = useState(false);

  // Map subscription types to plan keys
  const getCurrentPlanKey = () => {
    const tier = subscriptionStatus?.subscriptionType;
    const tierMap = {
      'NONE': 'free',
      'STARTER': 'starter',
      'PLUS': 'plus',
      'PRO': 'plus',
      'FAMILY': 'family',
      'UNLIMITED': 'unlimited',
    };
    return tierMap[tier] || 'free';
  };

  const currentPlanKey = getCurrentPlanKey();

  // Pricing data
  const plans = {
    free: {
      name: 'Free',
      monthlyPrice: 0,
      annualPrice: 0,
      stars: 50,
      features: [
        '50 stars/week',
        '60-second stories',
        'English only',
        'No downloads',
      ],
    },
    starter: {
      name: 'Starter',
      monthlyPrice: 7.99,
      annualPrice: 5.99,
      stars: 100,
      features: [
        '100 stars/week',
        '90-second stories',
        'All languages',
        'Download videos',
      ],
      badge: null,
    },
    plus: {
      name: 'Pro',
      monthlyPrice: 14.99,
      annualPrice: 11.99,
      stars: 250,
      features: [
        '250 stars/week',
        '90-second stories',
        'All languages',
        'Download videos',
      ],
      badge: 'MOST POPULAR',
    },
    family: {
      name: 'Family',
      monthlyPrice: 24.99,
      annualPrice: 18.99,
      stars: 650,
      features: [
        '650 stars/week',
        '90-second stories',
        'All languages',
        'Download videos',
        'Create up to 5 child profiles',
      ],
      badge: null,
    },
    unlimited: {
      name: 'Max',
      monthlyPrice: 49.99,
      annualPrice: 37.99,
      stars: 1500,
      features: [
        '1500 stars/week',
        '180-second stories',
        'All languages',
        'Download videos',
        'Create up to 5 child profiles',
      ],
      badge: 'BEST VALUE',
    },
  };

  const handleManageSubscription = async () => {
    if (!user?.id || !user?.email) return;

    try {
      setIsProcessing(true);
      showToast("Opening subscription portal...", "info");
      const response = await apiClient.createStripePortalSession(user.email);

      if (response.status && response.data?.url) {
        window.location.href = response.data.url;
      } else {
        showToast('Failed to open subscription management.', 'error');
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      showToast('Failed to open subscription management.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubscribe = async (tier) => {
    if (!user?.id || !user?.email) {
      showToast("Please log in to subscribe.", "error");
      return;
    }

    if (isProcessing) return;
    trackCheckoutStarted('subscription', tier);

    const hasActiveSubscription = currentPlanKey !== 'free';

    // Handle upgrade/downgrade for existing subscribers
    if (hasActiveSubscription && tier !== 'free') {
      const tierHierarchy = ['free', 'starter', 'plus', 'family', 'unlimited'];
      const currentTierIndex = tierHierarchy.indexOf(currentPlanKey);
      const newTierIndex = tierHierarchy.indexOf(tier);
      const isUpgrade = newTierIndex > currentTierIndex;

      const confirmed = window.confirm(
        isUpgrade
          ? "Upgrade your subscription? You'll be charged a prorated amount."
          : "Downgrade your subscription? The change takes effect immediately."
      );

      if (!confirmed) return;

      try {
        setIsProcessing(true);
        showToast(isUpgrade ? "Processing upgrade..." : "Processing downgrade...", "info");

        const response = await apiClient.updateStripeSubscription(
          user.id,
          tier,
          billingPeriod
        );

        if (response.status) {
          showToast(`Successfully ${isUpgrade ? 'upgraded' : 'downgraded'}! Reloading...`, "success");
          setTimeout(() => window.location.reload(), 1500);
        } else {
          throw new Error(response.error || "Failed to update subscription");
        }
      } catch (error) {
        console.error("Error updating subscription:", error);
        showToast("Failed to update subscription.", "error");
        setIsProcessing(false);
      }
      return;
    }

    // User is on Free tier - create new subscription
    try {
      setIsProcessing(true);
      showToast("Redirecting to checkout...", "info");

      const response = await apiClient.createStripeCheckoutSession(
        user.id,
        user.email,
        tier,
        billingPeriod
      );

      if (response.status && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error(response.error || "Failed to create checkout");
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      showToast("Failed to start checkout.", "error");
      setIsProcessing(false);
    }
  };

  const renderPlanCard = (planKey, plan) => {
    const isCurrentPlan = planKey === currentPlanKey;
    const price = billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    const originalPrice = plan.monthlyPrice;
    const showDiscount = billingPeriod === 'annual' && price < originalPrice;

    const tierHierarchy = ['free', 'starter', 'plus', 'family', 'unlimited'];
    const currentTierIndex = tierHierarchy.indexOf(currentPlanKey);
    const planTierIndex = tierHierarchy.indexOf(planKey);
    const isDowngrade = planTierIndex < currentTierIndex;
    const isUpgrade = planTierIndex > currentTierIndex;

    let ctaText = 'Get Started';
    let isDisabled = false;

    if (isCurrentPlan) {
      ctaText = 'Current Plan';
      isDisabled = true;
    } else if (isDowngrade) {
      ctaText = planKey === 'free' ? 'Cancel' : 'Downgrade';
    } else if (isUpgrade) {
      ctaText = 'Upgrade';
    }

    const tierColors = {
      free: { bg: 'from-gray-500/10 to-gray-600/10', border: 'border-gray-400/20', text: 'text-gray-300' },
      starter: { bg: 'from-emerald-500/10 to-teal-500/10', border: 'border-emerald-400/30', text: 'text-emerald-300' },
      plus: { bg: 'from-blue-500/10 to-indigo-500/10', border: 'border-blue-400/30', text: 'text-blue-300' },
      family: { bg: 'from-purple-500/10 to-pink-500/10', border: 'border-purple-400/30', text: 'text-purple-300' },
      unlimited: { bg: 'from-yellow-500/10 to-orange-500/10', border: 'border-yellow-400/30', text: 'text-yellow-300' },
    };

    const colors = tierColors[planKey];

    return (
      <div
        key={planKey}
        className={`relative rounded-2xl border ${colors.border} bg-gradient-to-br ${colors.bg} p-6 flex flex-col ${
          plan.badge === 'MOST POPULAR' ? 'scale-105 ring-2 ring-blue-400/30' : ''
        }`}
      >
        {plan.badge && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-bold shadow-lg">
            {plan.badge}
          </div>
        )}

        <div className="mb-4">
          <h3 className={`text-2xl font-bold ${colors.text} mb-2`}>{plan.name}</h3>
          {planKey !== 'free' ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-white">${price}</span>
                <span className="text-gray-400">/mo</span>
              </div>
              {showDiscount && (
                <div className="text-sm text-gray-400 line-through">${originalPrice}/mo</div>
              )}
            </>
          ) : (
            <div className="text-4xl font-bold text-white">$0</div>
          )}
        </div>

        <div className="flex-1 mb-6">
          <div className="space-y-3">
            {plan.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-300 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => handleSubscribe(planKey)}
          disabled={isDisabled || isProcessing}
          className={`w-full py-3 px-4 rounded-xl font-semibold transition-all ${
            isDisabled
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : `bg-gradient-to-r ${colors.bg.replace(/\/10/g, '')} hover:scale-105 text-white`
          }`}
        >
          {ctaText}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-32">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/settings"
            className="inline-flex items-center text-gray-400 hover:text-white mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Settings
          </Link>
          <h1 className="text-4xl font-bold text-white mb-2">Choose Your Plan</h1>
          <p className="text-gray-400">Get more stars every week and unlock premium features</p>
        </div>

        {/* Current Plan Indicator */}
        {currentPlanKey !== 'free' && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-400">Current Plan</div>
              <div className="text-xl font-bold text-white">{plans[currentPlanKey].name}</div>
            </div>
            <button
              onClick={handleManageSubscription}
              disabled={isProcessing}
              className="px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-lg transition-colors"
            >
              {isProcessing ? 'Opening...' : 'Manage Subscription'}
            </button>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              billingPeriod === 'monthly'
                ? 'bg-white text-gray-900'
                : 'bg-white/10 text-gray-400 hover:bg-white/20'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('annual')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              billingPeriod === 'annual'
                ? 'bg-white text-gray-900'
                : 'bg-white/10 text-gray-400 hover:bg-white/20'
            }`}
          >
            Annual <span className="text-green-400 text-sm ml-1">(Save 25%)</span>
          </button>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-12">
          {renderPlanCard('free', plans.free)}
          {renderPlanCard('starter', plans.starter)}
          {renderPlanCard('plus', plans.plus)}
          {renderPlanCard('family', plans.family)}
          {renderPlanCard('unlimited', plans.unlimited)}
        </div>

        {/* FAQ or Additional Info */}
        <div className="text-center text-gray-400 text-sm">
          All plans include characters and stories (powered by stars). Cancel anytime.
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSettings;
